import React, { useState, useEffect } from "react";
import toast from 'react-hot-toast';
import { proponerCitaDeVet, getDisponibilidadConfig } from "../../services/api";

// Brand colors - LemmonTags theme
const COLORS = {
  primary: "#84cc16",
  primaryDark: "#65a30d",
  primaryLight: "#ecfccb",
  background: "#fafafa",
  cardBg: "#ffffff",
  textDark: "#1f2937",
  textMuted: "#6b7280",
  border: "#e5e7eb",
  danger: "#dc2626"
};

// Tipos de servicio disponibles
const TIPOS_SERVICIO = [
  { value: "Vacunacion", label: "Vacunacion" },
  { value: "Desparasitacion", label: "Desparasitacion" },
  { value: "Consulta", label: "Consulta general" },
  { value: "Revision", label: "Revision de seguimiento" },
  { value: "Cirugia", label: "Cirugia" },
  { value: "Otro", label: "Otro" }
];

/**
 * Modal para que el veterinario proponga una cita a un paciente
 */
export default function ProponerCitaModal({
  isOpen,
  onClose,
  vetId,
  paciente,  // { objetoId, mascotaNombre, duenoId, duenoNombre, tipoSugerido }
  onSuccess
}) {
  const [loading, setLoading] = useState(false);
  const [loadingHorarios, setLoadingHorarios] = useState(false);
  const [disponibilidad, setDisponibilidad] = useState(null);

  // Form state
  const [fecha, setFecha] = useState("");
  const [horaInicio, setHoraInicio] = useState("");
  const [tipoConsulta, setTipoConsulta] = useState(paciente?.tipoSugerido || "Consulta");
  const [motivo, setMotivo] = useState("");

  // Horarios disponibles para la fecha seleccionada
  const [horariosDisponibles, setHorariosDisponibles] = useState([]);

  // Cargar configuración de disponibilidad al abrir
  useEffect(() => {
    if (isOpen && vetId) {
      cargarDisponibilidad();
      // Set initial values
      setTipoConsulta(paciente?.tipoSugerido || "Consulta");
      setMotivo(paciente?.tipoSugerido ? `${paciente.tipoSugerido} programada` : "");
    }
  }, [isOpen, vetId, paciente]);

  // Actualizar horarios cuando cambia la fecha
  useEffect(() => {
    if (fecha && disponibilidad) {
      calcularHorariosDisponibles();
    }
  }, [fecha, disponibilidad]);

  async function cargarDisponibilidad() {
    setLoadingHorarios(true);
    try {
      const config = await getDisponibilidadConfig(vetId);
      setDisponibilidad(config);
    } catch (error) {
      console.error("Error cargando disponibilidad:", error);
    } finally {
      setLoadingHorarios(false);
    }
  }

  function calcularHorariosDisponibles() {
    if (!fecha || !disponibilidad) {
      setHorariosDisponibles([]);
      return;
    }

    const date = new Date(fecha + "T12:00:00");
    const dias = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];
    const diaSemana = dias[date.getDay()];

    // Verificar si el día está activo
    if (!disponibilidad.diasActivos?.includes(diaSemana)) {
      setHorariosDisponibles([]);
      return;
    }

    // Verificar si es una excepción
    if (disponibilidad.excepciones?.includes(fecha)) {
      setHorariosDisponibles([]);
      return;
    }

    // Generar horarios basados en los turnos configurados
    const horarios = [];
    const turnos = disponibilidad.turnos || [];

    turnos.forEach(turno => {
      if (!turno.inicio || !turno.fin) return;

      let current = turno.inicio;
      while (current < turno.fin) {
        horarios.push(current);

        // Incrementar 30 minutos
        const [h, m] = current.split(":").map(Number);
        let newM = m + 30;
        let newH = h;
        if (newM >= 60) {
          newM = 0;
          newH++;
        }
        current = `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
      }
    });

    setHorariosDisponibles(horarios);
  }

  function getMinDate() {
    const today = new Date();
    return today.toISOString().split("T")[0];
  }

  function getMaxDate() {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split("T")[0];
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!fecha || !horaInicio) {
      toast.error("Selecciona fecha y hora");
      return;
    }

    setLoading(true);
    try {
      const citaData = {
        fecha,
        horaInicio,
        objetoId: paciente.objetoId,
        objetoName: paciente.mascotaNombre,
        ownerId: paciente.duenoId,
        tipoConsulta,
        motivo: motivo || `${tipoConsulta} para ${paciente.mascotaNombre}`
      };

      const resp = await proponerCitaDeVet(vetId, citaData);

      if (resp.error) {
        toast.error(resp.error);
      } else {
        toast.success(`Cita propuesta enviada a ${paciente.duenoNombre}`);
        onSuccess?.();
        handleClose();
      }
    } catch (error) {
      console.error("Error proponiendo cita:", error);
      toast.error("Error al proponer cita");
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    setFecha("");
    setHoraInicio("");
    setTipoConsulta("Consulta");
    setMotivo("");
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={handleClose}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content" style={{ borderRadius: "12px" }}>
          <div
            className="modal-header"
            style={{ backgroundColor: COLORS.primary, color: "white", borderRadius: "12px 12px 0 0" }}
          >
            <h5 className="modal-title" style={{ fontWeight: 600 }}>
              Proponer cita
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Info del paciente */}
              <div
                className="mb-3 p-3"
                style={{ backgroundColor: COLORS.primaryLight, borderRadius: "8px" }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: "24px" }}>🐾</span>
                  <div>
                    <strong style={{ color: COLORS.textDark }}>{paciente?.mascotaNombre}</strong>
                    <p className="mb-0" style={{ fontSize: "13px", color: COLORS.textMuted }}>
                      Dueno: {paciente?.duenoNombre}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tipo de servicio */}
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 500, fontSize: "14px" }}>
                  Tipo de servicio
                </label>
                <select
                  className="form-select"
                  value={tipoConsulta}
                  onChange={(e) => setTipoConsulta(e.target.value)}
                  style={{ fontSize: "14px" }}
                >
                  {TIPOS_SERVICIO.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                  ))}
                </select>
              </div>

              {/* Fecha */}
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 500, fontSize: "14px" }}>
                  Fecha
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  min={getMinDate()}
                  max={getMaxDate()}
                  style={{ fontSize: "14px" }}
                />
              </div>

              {/* Horarios disponibles */}
              {fecha && (
                <div className="mb-3">
                  <label className="form-label" style={{ fontWeight: 500, fontSize: "14px" }}>
                    Horario disponible
                  </label>

                  {loadingHorarios ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm" style={{ color: COLORS.primary }}></div>
                    </div>
                  ) : horariosDisponibles.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: "14px" }}>
                      No hay horarios disponibles para esta fecha. Verifica tu configuracion de disponibilidad.
                    </p>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {horariosDisponibles.map(hora => (
                        <button
                          key={hora}
                          type="button"
                          className="btn btn-sm"
                          style={{
                            backgroundColor: horaInicio === hora ? COLORS.primary : "white",
                            color: horaInicio === hora ? "white" : COLORS.textDark,
                            border: `1px solid ${horaInicio === hora ? COLORS.primary : COLORS.border}`,
                            fontSize: "13px"
                          }}
                          onClick={() => setHoraInicio(hora)}
                        >
                          {hora}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Motivo/Descripción */}
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 500, fontSize: "14px" }}>
                  Descripcion (opcional)
                </label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Descripcion o instrucciones para el dueno..."
                  style={{ fontSize: "14px" }}
                />
              </div>

              <p style={{ fontSize: "12px", color: COLORS.textMuted }}>
                Se enviara una notificacion al dueno para que confirme o rechace la cita.
              </p>
            </div>

            <div className="modal-footer" style={{ border: "none" }}>
              <button
                type="button"
                className="btn"
                style={{
                  backgroundColor: COLORS.cardBg,
                  color: COLORS.textDark,
                  border: `1px solid ${COLORS.border}`
                }}
                onClick={handleClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn"
                style={{
                  backgroundColor: COLORS.primary,
                  color: "white"
                }}
                disabled={loading || !fecha || !horaInicio}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Enviando...
                  </>
                ) : (
                  "Proponer cita"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
